import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  MessageSquareText, 
  FolderKanban, 
  FileSearch,
  Settings,
  ArrowRight,
  ShieldCheck,
  Brain,
  FileText,
  Bot,
  Check
} from 'lucide-react';

const LandingPage = () => {
  const { isAdmin } = useAuth();
  

  const mainFeatures = [
    {
      // Carte vedette
      title: "Assistants IA",
      subtitle: "Conversez en langage naturel",
      description: "Démarrez une conversation avec l’un de nos assistants IA spécialisés en fonction de votre besoin.",
      features: [
        "Assistants métiers spécialisés",
        "Détection automatique du besoin",
        "Réponses en temps réel",
      ],
      icon: MessageSquareText,
      link: "/avatars",
      color: "from-blue-500 to-indigo-600",
      badge: "Commencez ici !",
      buttonText: "Choisir un assistant",
      featured: true
    },
    {
      title: "Projets",
      subtitle: "Votre contexte de travail",
      description: "Conservez ici les informations relatives à vos projets et utilisez-les en contexte de vos conversations avec nos assistants IA.",
      features: [
        "Documents rattachés au projet",
        "Contexte injecté dans les assistants",
        "Partage avec votre équipe",
      ],
      icon: FolderKanban,
      link: "/projects",
      color: "from-emerald-500 to-teal-600",
      badge: "Dossiers",
      buttonText: "Ouvrir mes projets"
    },
    {
      title: "Documents",
      subtitle: "Bibliothèque & RAG",
      description: "Recherchez, résumez, analysez vos propres ressources documentaires et utilisez-les dans vos conversations avec nos Assistants IA.",
      features: [
        "Recherche en langage naturel",
        "Réponses sourcées",
        "Synthèses automatiques",
      ],
      icon: FileSearch,
      link: "/biblio-rag",
      color: "from-purple-500 to-violet-600",
      badge: "Documents",
      buttonText: "Lancer une recherche"
    }
  ];

  const secondaryFeatures = [
    {
      title: "Administration",
      description: "Gérez les utilisateurs, configurez les permissions et supervisez l'utilisation.",
      icon: Settings,
      link: "/users",
      color: "from-gray-500 to-slate-600"
    }
  ];

  const FeatureCard = ({ feature, size = "default", index = 0 }) => {
    const Icon = feature.icon;
    const isLarge = size === "large";
    const isFeatured = !!feature.featured;

    return (
      <Link
        to={feature.link}
        className={`
          arc-card-glass block
          ${isLarge ? 'p-8' : 'p-6'}
          group relative overflow-hidden flex flex-col
          ${isFeatured ? 'ring-2 ring-arc-accent-primary/40 shadow-lg' : ''}
        `}
      >
        {/* Fond dégradé : plus marqué pour la carte vedette */}
        <div className={`
          absolute inset-0 bg-gradient-to-br ${feature.color}
          ${isFeatured ? 'opacity-[12%] group-hover:opacity-[20%]' : 'opacity-[6%] group-hover:opacity-[14%]'}
          transition-opacity duration-300 rounded-[var(--arc-radius)]
        `} />

        {/* Badge : style dégradé uniforme pour toutes les cartes */}
        {feature.badge && (
          <div className="absolute top-4 right-4">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${feature.color} shadow`}>
              {feature.badge}
            </span>
          </div>
        )}

        {/* Icône */}
        <div className={`
          ${isLarge ? 'w-16 h-16 mb-6' : 'w-12 h-12 mb-4'}
          bg-gradient-to-br ${feature.color} rounded-xl
          flex items-center justify-center text-white
          group-hover:scale-110 transition-transform duration-300
        `}>
          <Icon size={isLarge ? 32 : 24} />
        </div>

        {/* Contenu */}
        <div className="relative z-10 flex flex-col flex-1">
          {/* Sous-titre / catégorie */}
          {feature.subtitle && (
            <span className="text-xs font-semibold uppercase tracking-wide text-arc-accent-primary mb-1">
              {feature.subtitle}
            </span>
          )}

          <h3 className={`
            ${isLarge ? 'text-2xl' : 'text-xl'}
            font-semibold text-arc-text-primary mb-3
            group-hover:text-arc-accent-primary transition-colors duration-300
          `}>
            {feature.title}
          </h3>

          <p className={`
            ${isLarge ? 'text-base' : 'text-sm'}
            text-arc-text-secondary mb-4 leading-relaxed
          `}>
            {feature.description}
          </p>

          {/* Liste de fonctionnalités à puces */}
          {feature.features?.length > 0 && (
            <ul className="space-y-2 mb-5">
              {feature.features.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-arc-text-secondary">
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center text-white`}>
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          )}

          {/* CTA aligné en bas de carte */}
          <span className="arc-btn-primary inline-flex items-center space-x-2 group/btn mt-auto self-start">
            <span>{feature.buttonText || (isLarge ? 'Commencer' : 'Découvrir')}</span>
            <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform duration-200" />
          </span>
        </div>
      </Link>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16">
      {/* Hero Section */}
      <div className="text-center mb-16 animate-fade-in-up">
        <div className="inline-flex items-center space-x-2 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-arc-accent-primary to-arc-accent-secondary rounded-xl flex items-center justify-center">
            <Bot className="text-white" size={24} />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-arc-text-primary">
            Bienvenue sur <span className="bg-gradient-to-r from-arc-accent-primary to-arc-accent-secondary bg-clip-text text-transparent">Chat Consult</span>
          </h1>
        </div>
        <h2 className="text-2xl lg:text-3xl font-semibold text-arc-text-primary mb-4">
          L'assistant IA des <span className="bg-gradient-to-r from-arc-accent-primary to-arc-accent-secondary bg-clip-text text-transparent">collectivités</span>
          {/* La plateforme d’<span className="bg-gradient-to-r from-arc-accent-primary to-arc-accent-secondary bg-clip-text text-transparent">assistants IA</span> au service de vos équipes */}
        </h2>
        <p className="text-lg text-arc-text-secondary max-w-3xl mx-auto leading-relaxed">
          Aide à la rédaction, recherche dans vos documents, assistance à la gestion de projet et au management, expertises métiers, conversez en langage naturel avec nos assistants IA personnalisés et spécialisés et gagnez en qualité, en productivité et en créativité dans vos tâches quotidiennes.
        </p>
      </div>

      {/* Main Features Grid */}
      <div className="relative mb-16">
        {/* Orbes de couleur pour le glassmorphism */}
        <div className="absolute -top-16 -left-16 w-72 h-72 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-[12%] blur-3xl pointer-events-none" />
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-80 h-80 bg-gradient-to-br from-purple-400 to-violet-500 rounded-full opacity-[8%] blur-3xl pointer-events-none" />
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-[12%] blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {mainFeatures.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              size="large"
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Secondary Features - Only for admins */}
      {isAdmin && (
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-arc-text-primary mb-3">
              Outils d'administration
            </h3>
            <p className="text-arc-text-secondary">
              Configurez et gérez votre environnement IA
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {secondaryFeatures.map((feature, index) => (
              <FeatureCard 
                key={feature.title} 
                feature={feature} 
                index={index + 3} 
              />
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="arc-card p-8 mb-16 animate-fade-in-up">
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-arc-text-primary mb-2">
            Pourquoi choisir Chat Consult ?
          </h3>
          <p className="text-arc-text-secondary">
            Une solution dédiée aux missions des collectivités.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="space-y-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white mx-auto">
              <Brain size={24} />
            </div>
            <h4 className="font-semibold text-arc-text-primary">IA Spécialisée</h4>
            <p className="text-sm text-arc-text-secondary">
              Nos assistants sont paramétrés spécifiquement pour les besoins des collectivités.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center text-white mx-auto">
              <ShieldCheck size={24} />
            </div>
            <h4 className="font-semibold text-arc-text-primary">Souveraineté</h4>
            <p className="text-sm text-arc-text-secondary">
              Sécurité et confidentialité de vos données, respect du cadre règlementaire.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center text-white mx-auto">
              <FileText size={24} />
            </div>
            <h4 className="font-semibold text-arc-text-primary">Base de connaissance</h4>
            <p className="text-sm text-arc-text-secondary">
              Utilisez vos propres ressources documentaires.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center animate-fade-in-up">
        <div className="arc-card p-8 bg-gradient-to-br from-arc-focus to-arc-surface border-arc-accent-primary/20">
          <h3 className="text-2xl font-semibold text-arc-text-primary mb-4">
            Prêt à commencer ?
          </h3>
          <p className="text-arc-text-secondary mb-6">
            Explorez nos assistants et découvrez comment l'IA peut transformer votre travail
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/assistants" className="arc-btn-primary arc-btn-lg">
              Essayer les assistants
            </Link>
            <Link to="/aide" className="arc-btn-secondary arc-btn-lg">
              Consulter l'aide
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;